import React, { useState } from "react";
import { useNode, useEditor } from "@craftjs/core";
import { 
  FormControl, 
  FormLabel, 
  TextField, 
  Select, 
  MenuItem, 
  Checkbox, 
  FormControlLabel,
  RadioGroup,
  Radio,
  Button,
  IconButton,
  Box
} from "@mui/material";
import { Delete as DeleteIcon, Add as AddIcon } from "@mui/icons-material";
import { useQwanta } from "qwanyx";

export const RadioGroupComponent = ({
  name,
  fontSize = 12,
  width = "100%",
  maxWidth,
  minWidth,
  flex,
  flexGrow,
  flexShrink,
  flexBasis,
  qwantumId,
  value = "",
  inputName,
  onSave,
  hidden,
  style,
  labelText = "",
  labelFontSize = 14,
  showSemicolon = true,
  labelBold = true,
  labelItalic = false,
  fontFamily = "inherit",
  answer = "",
  randomize = false,
  options = [
    { value: "option1", label: "Option 1" },
    { value: "option2", label: "Option 2" }
  ],
  orientation = "column",
  ...props
}) => {
  const { 
    connectors: { connect, drag },
    isSelected,
    isHovered,
    actions: { setProp }
  } = useNode((state) => ({
    isSelected: state.events.selected,
    isHovered: state.events.hovered,
  }));

  const { enabled } = useEditor((state) => ({
    enabled: state.options.enabled
  }));

  // Use useQwanta with proper conditional handling
  const [persistedValue, setPersistedValue] = useQwanta(qwantumId || "");
  const currentValue = (qwantumId && persistedValue !== null) ? persistedValue : value;

  const [shuffledOptions, setShuffledOptions] = useState(options);

  // Shuffle function
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Handle randomization on mount and when options/randomize changes
  React.useEffect(() => {
    if (randomize) {
      setShuffledOptions(shuffleArray(options));
    } else {
      setShuffledOptions(options);
    }
  }, [options, randomize]);

  if (hidden) return null;

  const handleChange = (e) => {
    const newValue = e.target.value;
    
    // Update CraftJS props when in editor mode
    if (enabled) {
      setProp(props => props.value = newValue);
    }
    
    // Handle persistence with safety checks
    if (qwantumId && qwantumId.trim() && setPersistedValue) {
      // Use requestAnimationFrame to defer the persistence call
      requestAnimationFrame(() => {
        try {
          setPersistedValue(qwantumId, newValue);
        } catch (error) {
          console.warn('Persistence failed:', error);
        }
      });
    }
    
    if (onSave) {
      onSave(newValue);
    }
  };

  // Selection styling - only show when editor is enabled
  const getSelectionStyle = () => {
    if (!enabled) return {};
    
    if (isSelected) {
      return {
        outline: '1px dashed #2196f3',
        outlineOffset: '2px'
      };
    }
    if (isHovered) {
      return {
        outline: '1px dashed #ff9800',
        outlineOffset: '1px'
      };
    }
    return {};
  };

  const formatSize = (value) => {
    if (!value) return undefined;
    if (typeof value === 'string') return value;
    return `${value}px`;
  };

  return (
    <div 
      ref={ref => enabled ? connect(drag(ref)) : connect(ref)}
      name={name}
      style={{
        transition: 'all 0.2s ease',
        position: 'relative',
        width: formatSize(width),
        maxWidth: formatSize(maxWidth),
        minWidth: formatSize(minWidth),
        flex: flex,
        flexGrow: flexGrow,
        flexShrink: flexShrink,
        flexBasis: flexBasis,
        boxSizing: 'border-box',
        ...style,
        ...getSelectionStyle()
      }}
      {...props}
    >
      {labelText && (
        <label
          style={{
            display: 'block',
            fontSize: `${labelFontSize}px`,
            fontFamily: fontFamily,
            fontWeight: labelBold ? 'bold' : 'normal',
            fontStyle: labelItalic ? 'italic' : 'normal',
            marginBottom: '8px',
            color: '#333'
          }}
        >
          {labelText}{showSemicolon ? ':' : ''}
        </label>
      )}
      <FormControl>
        <RadioGroup
          name={inputName}
          value={currentValue}
          onChange={handleChange}
          row={orientation === "row"}
          style={{
            fontSize: `${fontSize}px`,
            fontFamily: fontFamily
          }}
        >
          {shuffledOptions.map((option, index) => (
            <FormControlLabel
              key={option.value}
              value={option.value}
              control={<Radio />}
              label={option.label}
              style={{
                fontSize: `${fontSize}px`,
                fontFamily: fontFamily
              }}
            />
          ))}
        </RadioGroup>
      </FormControl>
    </div>
  );
};

const RadioGroupSettings = () => {
  const { 
    actions: { setProp }, 
    name, fontSize, width, maxWidth, minWidth,
    flex, flexGrow, flexShrink, flexBasis,
    qwantumId, inputName, value,
    labelText, labelFontSize, showSemicolon, labelBold, labelItalic, fontFamily,
    answer, randomize, options, orientation
  } = useNode((node) => ({
    name: node.data.props.name,
    fontSize: node.data.props.fontSize,
    width: node.data.props.width,
    maxWidth: node.data.props.maxWidth,
    minWidth: node.data.props.minWidth,
    flex: node.data.props.flex,
    flexGrow: node.data.props.flexGrow,
    flexShrink: node.data.props.flexShrink,
    flexBasis: node.data.props.flexBasis,
    qwantumId: node.data.props.qwantumId,
    inputName: node.data.props.inputName,
    value: node.data.props.value,
    labelText: node.data.props.labelText,
    labelFontSize: node.data.props.labelFontSize,
    showSemicolon: node.data.props.showSemicolon,
    labelBold: node.data.props.labelBold,
    labelItalic: node.data.props.labelItalic,
    fontFamily: node.data.props.fontFamily,
    answer: node.data.props.answer,
    randomize: node.data.props.randomize,
    options: node.data.props.options,
    orientation: node.data.props.orientation
  }));

  const handleFlexChange = (prop, value) => {
    if (value === '') {
      setProp(props => props[prop] = undefined);
    } else {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        setProp(props => props[prop] = numValue);
      }
    }
  };

  const addOption = () => {
    setProp(props => {
      props.options = [...props.options, { value: `option${props.options.length + 1}`, label: `Option ${props.options.length + 1}` }];
    });
  };

  const removeOption = (index) => {
    setProp(props => {
      props.options = props.options.filter((_, i) => i !== index);
    });
  };

  const updateOption = (index, field, value) => {
    setProp(props => {
      props.options[index][field] = value;
    });
  };

  const handleAnswerChange = (optionValue) => {
    setProp(props => props.answer = optionValue);
  };

  return (
    <div>
      <FormControl fullWidth margin="normal">
        <FormLabel>Component Name</FormLabel>
        <TextField
          value={name || ''}
          onChange={(e) => setProp(props => props.name = e.target.value)}
          size="small"
          placeholder="Enter component name"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Reset Selection</FormLabel>
        <Button
          variant="outlined"
          color="secondary"
          onClick={() => setProp(props => props.value = "")}
          size="small"
        >
          Clear Selection
        </Button>
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Label Text</FormLabel>
        <TextField
          value={labelText || ''}
          onChange={(e) => setProp(props => props.labelText = e.target.value)}
          size="small"
          placeholder="Enter label text"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Quantum ID</FormLabel>
        <TextField
          value={qwantumId || ''}
          onChange={(e) => setProp(props => props.qwantumId = e.target.value)}
          size="small"
          placeholder="Enter quantum ID for persistence"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Input Name</FormLabel>
        <TextField
          value={inputName || ''}
          onChange={(e) => setProp(props => props.inputName = e.target.value)}
          size="small"
          placeholder="Enter input name attribute"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Orientation</FormLabel>
        <Select
          value={orientation || 'column'}
          onChange={(e) => setProp(props => props.orientation = e.target.value)}
          size="small"
        >
          <MenuItem value="column">Vertical</MenuItem>
          <MenuItem value="row">Horizontal</MenuItem>
        </Select>
      </FormControl>

      <FormControlLabel
        control={
          <Checkbox
            checked={randomize}
            onChange={(e) => setProp(props => props.randomize = e.target.checked)}
          />
        }
        label="Randomize option order (shuffle on load)"
      />

      {/* Radio Options Management */}
      <FormControl fullWidth margin="normal">
        <FormLabel>Radio Options (Click radio to set correct answer)</FormLabel>
        <Box>
          {options?.map((option, index) => (
            <Box key={index} display="flex" alignItems="center" gap={1} marginBottom={1}>
              <Radio
                checked={answer === option.value}
                onChange={() => handleAnswerChange(option.value)}
                size="small"
              />
              <TextField
                size="small"
                placeholder="Value"
                value={option.value}
                onChange={(e) => updateOption(index, 'value', e.target.value)}
                style={{ flex: 1 }}
              />
              <TextField
                size="small"
                placeholder="Label"
                value={option.label}
                onChange={(e) => updateOption(index, 'label', e.target.value)}
                style={{ flex: 2 }}
              />
              <IconButton
                size="small"
                onClick={() => removeOption(index)}
                disabled={options.length <= 1}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}
          <Button
            startIcon={<AddIcon />}
            onClick={addOption}
            size="small"
            variant="outlined"
          >
            Add Option
          </Button>
        </Box>
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Label Font Size</FormLabel>
        <TextField
          value={labelFontSize || 14}
          onChange={(e) => setProp(props => props.labelFontSize = parseInt(e.target.value) || 14)}
          size="small"
          type="number"
          inputProps={{ min: 8, max: 72 }}
        />
      </FormControl>

      <FormControlLabel
        control={
          <Checkbox
            checked={labelBold}
            onChange={(e) => setProp(props => props.labelBold = e.target.checked)}
          />
        }
        label="Bold label"
      />

      <FormControlLabel
        control={
          <Checkbox
            checked={labelItalic}
            onChange={(e) => setProp(props => props.labelItalic = e.target.checked)}
          />
        }
        label="Italic label"
      />

      <FormControl fullWidth margin="normal">
        <FormLabel>Font Family</FormLabel>
        <Select
          value={fontFamily || 'inherit'}
          onChange={(e) => setProp(props => props.fontFamily = e.target.value)}
          size="small"
        >
          <MenuItem value="inherit">Inherit</MenuItem>
          <MenuItem value="Arial, sans-serif">Arial</MenuItem>
          <MenuItem value="Helvetica, sans-serif">Helvetica</MenuItem>
          <MenuItem value="'Times New Roman', serif">Times New Roman</MenuItem>
          <MenuItem value="Georgia, serif">Georgia</MenuItem>
          <MenuItem value="'Courier New', monospace">Courier New</MenuItem>
          <MenuItem value="Verdana, sans-serif">Verdana</MenuItem>
        </Select>
      </FormControl>

      <FormControlLabel
        control={
          <Checkbox
            checked={showSemicolon}
            onChange={(e) => setProp(props => props.showSemicolon = e.target.checked)}
          />
        }
        label="Show semicolon after label"
      />

      <FormControl fullWidth margin="normal">
        <FormLabel>Font Size</FormLabel>
        <TextField
          value={fontSize || 12}
          onChange={(e) => setProp(props => props.fontSize = parseInt(e.target.value) || 12)}
          size="small"
          type="number"
          inputProps={{ min: 8, max: 72 }}
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Width</FormLabel>
        <TextField
          value={width || ''}
          onChange={(e) => setProp(props => props.width = e.target.value)}
          size="small"
          placeholder="100%, 300px, auto, or leave empty"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Max Width</FormLabel>
        <TextField
          value={maxWidth || ''}
          onChange={(e) => setProp(props => props.maxWidth = e.target.value)}
          size="small"
          placeholder="400px, 50%, or leave empty"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Min Width</FormLabel>
        <TextField
          value={minWidth || ''}
          onChange={(e) => setProp(props => props.minWidth = e.target.value)}
          size="small"
          placeholder="100px, 20%, or leave empty"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Flex</FormLabel>
        <TextField
          value={flex || ''}
          onChange={(e) => handleFlexChange('flex', e.target.value)}
          size="small"
          placeholder="1, 0 1 auto, or leave empty"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Flex Grow</FormLabel>
        <TextField
          value={flexGrow || ''}
          onChange={(e) => handleFlexChange('flexGrow', e.target.value)}
          size="small"
          placeholder="0, 1, 2, or leave empty"
          type="number"
          inputProps={{ min: 0, step: 1 }}
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Flex Shrink</FormLabel>
        <TextField
          value={flexShrink || ''}
          onChange={(e) => handleFlexChange('flexShrink', e.target.value)}
          size="small"
          placeholder="1, 0, or leave empty"
          type="number"
          inputProps={{ min: 0, step: 1 }}
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Flex Basis</FormLabel>
        <TextField
          value={flexBasis || ''}
          onChange={(e) => setProp(props => props.flexBasis = e.target.value || undefined)}
          size="small"
          placeholder="auto, 200px, 50%, or leave empty"
        />
      </FormControl>
    </div>
  );
};

RadioGroupComponent.craft = {
  props: {
    name: "",
    fontSize: 12,
    width: "100%",
    maxWidth: null,
    minWidth: null,
    flex: null,
    flexGrow: null,
    flexShrink: null,
    flexBasis: null,
    qwantumId: "",
    value: "",
    inputName: "",
    labelText: "",
    labelFontSize: 14,
    showSemicolon: true,
    labelBold: true,
    labelItalic: false,
    fontFamily: "inherit",
    answer: "",
    randomize: false,
    options: [
      { value: "option1", label: "Option 1" },
      { value: "option2", label: "Option 2" }
    ],
    orientation: "column"
  },
  related: {
    settings: RadioGroupSettings
  }
};